import { POS_WS_API } from '../app';
import { IS_POS } from '@/utils/env';
import { useCallback, useEffect, useRef, useState } from 'react';

export const DOMAIN_PAYMENT = 'PAYMENT';
export const DOMAIN_POS = 'POS';
export const DOMAIN_STOCK = 'STOCK';
export const ONLINE_ORDER = 'ONLINE_ORDER';
export const DOMAIN_INTERACT_OUTSIDE = 'INTERACT_OUTSIDE';

export interface CommonTextTipMsg {
  level: 'warn' | 'success' | 'info' | 'error';
  text: string;
  key: string;
}

export enum WS_CLOSE_REASON {
  /** 已在其它设备登录 */
  SESSION_DUPLICATED = 'SESSION_DUPLICATED',
  /** session 过期 */
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  TERMINATE = 'TERMINATE',
}

export enum WsActions {
  COMMON_TEXT_TIP = 0,

  PAYING_TIP = 11000,

  POI_CONFIG_UPDATE = 10,

  POS_DATA_VERSION = 100,

  CATEGORY_ADD = 1001,
  CATEGORY_UPDATE = 1002,
  CATEGORY_DELETE = 1003,
  CATEGORY_ORDER_CHANGE = 1004,

  GOODS_ADD = 1110,
  GOODS_UPDATE = 1120,
  GOODS_UPDATE_ALL = 1121,
  GOODS_DELETE = 1130,

  STOCK_UPDATE = 1180,

  CARD_ADD = 1210,
  CARD_UPDATE = 1220,
  CARD_DELETE = 1230,

  POINT_RULE_CHANGE = 1250,

  // 小程序订单支付
  WEAPP_ORDER_PAY = 10001,

  //  小程序联动
  WEAPP_USER_VISIT_IN_POI = 20001,
}

export type WsInMsg = {
  domain: string;
  action: WsActions;
  data: unknown;
};

type Listener<T> = {
  (data: T, domain: string, action: WsActions): void | true;
};

const listeners: {
  [domainAndAction: string]: Listener<unknown>[];
} = {};

type SocketListener = {
  open: () => void;
  close: () => void;
  error: () => void;
  pong: (delay: number) => void;
  expired: () => void;
};

const socketListeners: {
  open: SocketListener['open'][];
  close: SocketListener['close'][];
  error: SocketListener['error'][];
  pong: SocketListener['pong'][];
  expired: SocketListener['expired'][];
} = {
  open: [],
  close: [],
  error: [],
  pong: [],
  expired: [],
};

export const addSocketListener = (type: keyof SocketListener, listener: (...args) => void) => {
  socketListeners[type].push(listener);
  return `${type}-${socketListeners[type].length - 1}`;
};

export const removeSocketListener = (key: string) => {
  const [type, index] = key.split('-');
  socketListeners[type].splice(index, 1);
};

const triggerSocketListener = (type: keyof SocketListener, ...args) => {
  const listeners = socketListeners[type];
  for (let listener of listeners) {
    // @ts-ignore
    listener(...args);
  }
};

let socket: WebSocket;
let wsPingPongDelay: number;
let isConnecting = false;
let reconnectAttempts = 0;

const HEARTBEAT_INTERVAL = 10000;
const HEARTBEAT_TIMEOUT = 5000;
const MAX_RECONNECT_DELAY = 30000;

const createSocketConnection = () => {
  if (isConnecting || (socket && socket.readyState === WebSocket.OPEN)) {
    return;
  }

  // 熄屏状态不连接
  if (IS_POS && window.__POS_Device__.isScreenOff?.() === true) {
    setTimeout(() => {
      createSocketConnection();
    }, 1000);
    return;
  }

  isConnecting = true;
  socket = new WebSocket(`${POS_WS_API}/websocket`);

  let heartbeatTimer: NodeJS.Timeout;
  let disconnectTimer: NodeJS.Timeout;
  // 上一次发送心跳检测的时间
  let timePing: number;

  const clearTimers = () => {
    clearTimeout(heartbeatTimer);
    clearTimeout(disconnectTimer);
  };

  const sendPing = () => {
    if (socket.readyState !== WebSocket.OPEN) return;
    timePing = new Date().getTime();
    socket.send('ping');
    disconnectTimer = setTimeout(() => {
      console.warn('ws 心跳超时, 主动断开...');
      socket.close();
    }, HEARTBEAT_TIMEOUT);
  };

  const startHeartbeat = () => {
    clearTimeout(heartbeatTimer);
    heartbeatTimer = setTimeout(() => {
      sendPing();
    }, HEARTBEAT_INTERVAL);
  };

  // Connection opened
  socket.addEventListener('open', function (event) {
    isConnecting = false;

    if (reconnectAttempts > 0) {
      console.info('ws 重新连接成功...');
    }

    reconnectAttempts = 0; // 重置重连次数

    triggerSocketListener('open');
    sendPing(); // 建立连接后立即发送心跳
  });

  // Listen for messages
  socket.addEventListener('message', function (event) {
    if (event && event.data === 'pong') {
      // 心跳检测
      triggerSocketListener('pong', (wsPingPongDelay = new Date().getTime() - timePing));
      clearTimeout(disconnectTimer);
      startHeartbeat(); // 收到 pong 后开启下一轮倒计时
      return;
    }
    try {
      const msg = JSON.parse(event.data) as WsInMsg;
      const listenerKey = `${msg.domain}-${msg.action}`;
      const domainWildcardKey = `${msg.domain}-*`;

      if (listeners.hasOwnProperty(listenerKey) && listeners[listenerKey].length) {
        for (let i = listeners[listenerKey].length - 1; i >= 0; i--) {
          const listener = listeners[listenerKey][i];
          // 可能为 null
          if (listener) {
            try {
              const stopPropagation = listener(msg.data, msg.domain, msg.action);
              // 阻止事件传播
              if (stopPropagation === true) {
                break;
              }
            } catch (e) {
              console.error('ws listener 执行异常: ', e);
            }
          }
        }
      } else if (listeners.hasOwnProperty(domainWildcardKey) && listeners[domainWildcardKey].length) {
        for (let i = listeners[domainWildcardKey].length - 1; i >= 0; i--) {
          const listener = listeners[domainWildcardKey][i];
          if (listener) {
            try {
              const stopPropagation = listener(msg.data, msg.domain, msg.action);
              // 阻止事件传播
              if (stopPropagation === true) {
                break;
              }
            } catch (e) {
              console.error('ws wildcard listener 执行异常: ', e);
            }
          }
        }
      } else {
        console.warn('ws 消息分发失败: 未监听', '原始信息' + event.data);
      }
    } catch (e) {
      console.error('ws 消息分发失败: ', e, `原始信息: ${event.data}`);
    }
  });

  socket.addEventListener('close', function (event) {
    isConnecting = false;
    socket = null;
    clearTimers();

    triggerSocketListener('close', {
      reason: event.reason,
    });

    if (
      event.reason === WS_CLOSE_REASON.SESSION_DUPLICATED ||
      event.reason === WS_CLOSE_REASON.SESSION_EXPIRED ||
      event.reason === WS_CLOSE_REASON.TERMINATE
    ) {
      // 这些情况不需要重连
      return;
    }

    const timeout = Math.min(1000 * Math.pow(2, reconnectAttempts), MAX_RECONNECT_DELAY);
    reconnectAttempts++;

    setTimeout(() => {
      createSocketConnection();
    }, timeout);
    console.log(`ws 断开, ${timeout}ms 后尝试重新连接`);
  });

  socket.addEventListener('error', function (event) {
    isConnecting = false;
    clearTimers();
    if (socket) {
      socket.close();
    }
    triggerSocketListener('error');
    console.log('ws error...');
  });
};

/**
 * 连接状态监控
 */
export const useWsStatusMonitor = () => {
  const mounted = useRef(true);
  const [connected, setConnected] = useState(!!socket);
  const [delay, setDelay] = useState<number>(wsPingPongDelay);
  useEffect(() => {
    const open = addSocketListener('open', () => {
      setConnected(true);
    });
    const close = addSocketListener('close', ({ reason }) => {
      console.log('ws close, reason:', reason);
      setConnected(false);
    });
    const error = addSocketListener('error', () => {
      console.log('ws error...');
      if (mounted) {
        setConnected(false);
      }
    });
    const pong = addSocketListener('pong', delay => {
      if (mounted) {
        setDelay(delay);
      }
    });

    return () => {
      removeSocketListener(open);
      removeSocketListener(close);
      removeSocketListener(error);
      removeSocketListener(pong);
      mounted.current = false;
    };
  }, []);

  return {
    delay,
    connected,
  };
};

/**
 * 初始化
 */
export const useWsConnection = ({
  onKickAway = () => {},
}: {
  onKickAway?: (reason: WS_CLOSE_REASON) => void;
} = {}) => {
  const mounted = useRef(true);

  // 重连
  const connect = useCallback(() => {
    createSocketConnection();
  }, []);

  // 主动断开 (不会自动重连)
  const terminate = () => {
    if (socket) {
      socket.close(1000, WS_CLOSE_REASON.TERMINATE);
    }
  };

  useEffect(() => {
    const close = addSocketListener('close', ({ reason }) => {
      if (reason === WS_CLOSE_REASON.SESSION_DUPLICATED || reason === WS_CLOSE_REASON.SESSION_EXPIRED) {
        if (mounted) {
          onKickAway(reason);
        }
      }
    });
    connect();
    return () => {
      removeSocketListener(close);
      mounted.current = false;
    };
  }, []);

  return {
    reconnect: connect,
    terminate,
  };
};

/**
 * 添加 ws 消息监听器
 * @param domain
 * @param action
 * @param listener
 */
export const addWsListener: <T>(
  domain: WsInMsg['domain'],
  action: WsInMsg['action'] | '*',
  listener: Listener<T>
) => string = (domain, action, listener) => {
  const key = `${domain}-${action}`;
  if (!listeners.hasOwnProperty(key)) {
    listeners[key] = [];
  }
  listeners[key].push(listener);
  return `${domain}-${action}-${listeners[key].length - 1}`;
};

export const removeWsListener = (id: string) => {
  const [domain, action, index] = id.split('-');
  try {
    listeners[`${domain}-${action}`][+index] = null;
  } catch (e) {}
};

export interface PosChangeIncomingMsg<T> {
  modifier?: string;
  version: number;
  payload: T;
  time: string;
}

export const addPOSChangeListener = <T>(listener: Listener<PosChangeIncomingMsg<T>>) => {
  return addWsListener<PosChangeIncomingMsg<T>>(DOMAIN_POS, '*', listener);
};

// @ts-ignore
if (module && module.hot) {
  // @ts-ignore
  if (module.hot.data && module.hot.data.socket) {
    // HMR: 检测到从旧模块传递过来的 socket，强制关闭它
    // @ts-ignore
    const oldSocket = module.hot.data.socket;
    try {
      console.log('[HMR] Replace: Closing orphaned socket from previous module version');
      oldSocket.close(1000, WS_CLOSE_REASON.TERMINATE);
    } catch (e) {}
  }

  // @ts-ignore
  module.hot.dispose(data => {
    if (socket) {
      console.log('[HMR] Dispose: Cleaning up active WebSocket connection...');
      // 1. 尝试主动关闭
      socket.close(1000, WS_CLOSE_REASON.TERMINATE);
      // 2. 将引用传递给下一个模块，作为双重保险 (新模块初始化时会再次检查并关闭)
      data.socket = socket;
      socket = null;
    }
  });
}
